var _excluded = ["buttons", "button", "duration", "variant", "Icon", "children", "callback", "callbacks", "closeWhenClick", "remove", "showIcon"];

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React from 'react';
import * as Announce from '@radix-ui/react-announce';
import { styled, forwardRef } from '@grupoboticario/flora-react-system';
import { Box } from '@grupoboticario/flora-react-box';
import { Text } from '@grupoboticario/flora-react-text';
import { ThumbUpOutlineIcon, CrossCircleIcon, ExclamationMarkChatBubbleIcon, PersonCircleIcon } from '@grupoboticario/flora-react-icons';
import { motion } from 'framer-motion';
import { useMeasure } from '@grupoboticario/flora-react-hooks';
export var StyledToastButton = styled('button', {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  appearance: 'none',
  background: 'transparent',
  userSelect: 'none',
  outline: 'none',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
  padding: '0 $2',
  color: '$actionableAlt',
  fontSize: '$bodySmall',
  lineHeight: '16px',
  transition: "\n  background 150ms ease,\n    box-shadow 150ms ease-in-out,\n    color 150ms ease\n",
  '&::before, &::after': {
    boxSizing: 'border-box'
  },
  '&:active': {
    opacity: '0.8'
  },
  '&:focus': {
    boxShadow: '$outline'
  }
});
export var StyledToast = styled('div', {
  boxSizing: 'border-box',
  color: '$nonInteractiveAltPredominant',
  borderRadius: '$small',
  boxShadow: '$downFar',
  display: 'flex',
  flexDirection: 'row',
  gap: '$2',
  userSelect: 'none',
  position: 'relative',
  padding: '$3',
  '& > svg': {
    marginRight: '$2',
    width: '16px',
    height: 'auto'
  },
  '& svg path': {
    fill: '$nonInteractiveAltPredominant'
  },
  variants: {
    variant: {
      neutral: {
        background: '$backgroundAltSecondary',
        ["& " + StyledToastButton]: {
          color: '$colors$linkAlt'
        }
      },
      success: {
        background: '$success-standard'
      },
      alert: {
        background: '$alert-standard'
      },
      error: {
        background: '$error-standard'
      }
    }
  },
  defaultVariants: {
    variant: 'neutral'
  }
});
var StyledToastTimeBar = styled('span', {
  width: '100%',
  height: '3px',
  position: 'absolute',
  bottom: 0,
  left: 0,
  background: '$disabledBackground',
  borderRadius: '0 0 $small $small',
  overflow: 'hidden',
  '& > span': {
    height: '3px',
    display: 'block'
  },
  variants: {
    variant: {
      success: {
        '& > span': {
          background: '$success-alt'
        }
      },
      neutral: {
        '& > span': {
          background: '$disabledAltBackground'
        }
      },
      alert: {
        '& > span': {
          background: '$alert-alt'
        }
      },
      error: {
        '& > span': {
          background: '$error-alt'
        }
      }
    }
  }
});
var ToastIcons = {
  success: /*#__PURE__*/React.createElement(ThumbUpOutlineIcon, {
    size: 20
  }),
  error: /*#__PURE__*/React.createElement(CrossCircleIcon, {
    size: 20
  }),
  alert: /*#__PURE__*/React.createElement(ExclamationMarkChatBubbleIcon, {
    size: 20
  }),
  neutral: /*#__PURE__*/React.createElement(PersonCircleIcon, {
    size: 20
  })
};

/**
 * @component
 * @name Toast
 * @description Toast component
 * @tutorial https://flora.gb.tech/docs/components/core/toast
 */
export var Toast = forwardRef((_ref, forwardedRef) => {
  var {
    buttons = [],
    button,
    duration = 5000,
    variant = 'neutral',
    Icon: CustomIcon,
    children,
    callback = () => {},
    callbacks = [() => {}, () => {}],
    closeWhenClick = true,
    remove = () => {},
    showIcon
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);

  var CurrentIcon = ToastIcons[variant];
  var toastIcon = showIcon ? CustomIcon || CurrentIcon : null;
  return /*#__PURE__*/React.createElement(StyledToast, _extends({
    ref: forwardedRef,
    "data-flora": "toast",
    variant: variant,
    css: _extends({}, !!(buttons != null && buttons.length) && {
      flexDirection: 'column',
      flexWrap: 'wrap'
    })
  }, rest), /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    css: {
      display: 'flex',
      gap: '$2'
    }
  }, /*#__PURE__*/React.createElement(React.Fragment, null, toastIcon, /*#__PURE__*/React.createElement(Announce.Root, {
    asChild: true
  }, /*#__PURE__*/React.createElement(Text, {
    as: "span",
    css: {
      fontSize: '$bodySmall'
    }
  }, children)), button && !(buttons != null && buttons.length) && /*#__PURE__*/React.createElement(StyledToastButton, {
    onClick: () => {
      if (closeWhenClick && remove) {
        remove();
      }

      if (typeof callback === 'function') {
        callback();
      }
    },
    "data-flora": "toast-action-1"
  }, button))), !!buttons.length && /*#__PURE__*/React.createElement(Box, {
    css: {
      display: 'flex',
      justifyContent: 'flex-end'
    }
  }, buttons.map((currentButton, index) => /*#__PURE__*/React.createElement(StyledToastButton, {
    key: currentButton,
    onClick: () => {
      if (closeWhenClick && remove) {
        remove();
      }

      if (typeof callbacks[index] === 'function') {
        callbacks[index]();
      }
    },
    "data-flora": "toast-action-" + (index + 1)
  }, currentButton))), variant && /*#__PURE__*/React.createElement(StyledToastTimeBar, {
    variant: variant
  }, /*#__PURE__*/React.createElement(motion.span, {
    initial: {
      width: '0%'
    },
    animate: {
      width: '100%'
    },
    transition: {
      duration: duration / 1000
    }
  }))));
});
export var ToastWrapper = _ref2 => {
  var {
    children,
    invert = false
  } = _ref2;
  var [measureRef, {
    height
  }] = useMeasure();
  var initalY = invert ? 200 : -200;
  var exitY = invert ? -height : 0;
  return /*#__PURE__*/React.createElement(motion.div, {
    initial: {
      y: initalY,
      height: 0,
      scale: 0.9
    },
    animate: {
      scale: 1,
      height,
      y: 0
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      height: 0,
      y: exitY
    },
    transition: {
      duration: 0.3
    }
  }, /*#__PURE__*/React.createElement(Box, {
    ref: measureRef
  }, /*#__PURE__*/React.createElement(Box, {
    css: {
      boxSizing: 'border-box',
      padding: '$1 $2'
    }
  }, children)));
};
//# sourceMappingURL=Toast.js.map